import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Autocomplete,
    Card,
    Grid,
    CircularProgress,
    Divider
} from "@mui/material";

import { db } from "../../firebase";

import {
    collection,
    getDocs,
    query,
    where,
    Timestamp,
    orderBy,
    onSnapshot
} from "firebase/firestore";

function ProductwiseReport() {

    const today = new Date().toISOString().split("T")[0];

    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);

    const [productList, setProductList] = useState([]);
    const [customerList, setCustomerList] = useState([]);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    // ================= PRODUCTS =================
    useEffect(() => {
        const unsub = onSnapshot(collection(db, "trip_products"), (snap) => {
            const prods = snap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setProductList(prods);
        });

        return () => unsub();
    }, []);

    // ================= CUSTOMERS =================
    useEffect(() => {
        const unsub = onSnapshot(collection(db, "customers"), (snap) => {
            const customers = snap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setCustomerList(customers);
        });

        return () => unsub();
    }, []);

    // ================= FETCH REPORT =================
    const fetchReport = async () => {

        if (!fromDate || !toDate) {
            return alert("Please select date range");
        }

        setLoading(true);

        try {

            const start = Timestamp.fromDate(
                new Date(`${fromDate}T00:00:00`)
            );

            const end = Timestamp.fromDate(
                new Date(`${toDate}T23:59:59`)
            );

            const q = query(
                collection(db, "trips"),
                where("grossDate", ">=", start),
                where("grossDate", "<=", end),
                orderBy("grossDate", "asc")
            );

            const querySnapshot = await getDocs(q);

            const allTrips = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            let flattenedProducts = [];

            allTrips.forEach(trip => {

                const tripProducts = trip.products || [];

                // ================= CUSTOMER FILTER =================
                const customerMatch =
                    !selectedCustomer ||
                    trip.customerId === selectedCustomer.id;

                if (!customerMatch) return;

                tripProducts.forEach(p => {

                    // ================= PRODUCT FILTER =================
                    const productMatch =
                        !selectedProduct ||
                        p.productId === selectedProduct.id;

                    if (productMatch) {

                        flattenedProducts.push({

                            tripId: trip.id,

                            ticketNo: trip.ticketNo || "-",

                            tareDate: trip.tareDate,
                            grossDate: trip.grossDate,

                            vehicleNo: trip.vehicleNo || "N/A",

                            partyName:
                                trip.partyName ||
                                trip.customerName ||
                                "General",

                            productName: p.productName,

                            qty: Number(p.qty) || 0,

                            rate: Number(p.rate) || 0,

                            amount: Number(p.amount) || 0,

                            tareWeight: Number(trip.tareWeight) || 0,
                            grossWeight: Number(trip.grossWeight) || 0,
                            netWeight: Number(trip.netWeight) || 0

                        });
                    }
                });
            });

            setReportData(flattenedProducts);

        } catch (error) {

            console.error("Error fetching report:", error);

            alert("Error loading report. Check console.");

        } finally {

            setLoading(false);
        }
    };

    // ================= TOTALS =================
    const totalQty = reportData.reduce(
        (sum, item) => sum + item.qty,
        0
    );

    const totalAmt = reportData.reduce(
        (sum, item) => sum + item.amount,
        0
    );

    const totalTare = reportData.reduce(
        (sum, item) => sum + item.tareWeight,
        0
    );

    const totalGross = reportData.reduce(
        (sum, item) => sum + item.grossWeight,
        0
    );

    const totalNet = reportData.reduce(
        (sum, item) => sum + item.netWeight,
        0
    );

    const hasBolder = reportData.some(
        row => row.productName === "Bolder"
    );

    const totalTrips = new Set(
        reportData.map(item => item.tripId)
    ).size;

    return (
        <Box p={3}>

            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Product-wise Trip Report
            </Typography>

            {/* ================= FILTERS ================= */}

            <Grid
                spacing={2}
                alignItems="center"
                className="no-print row"
            >

                {/* PRODUCT */}
                <Grid className="col-sm-3 mb-2">

                    <Autocomplete
                        options={productList}
                        getOptionLabel={(option) =>
                            option.productName || ""
                        }
                        value={selectedProduct}
                        onChange={(event, newValue) =>
                            setSelectedProduct(newValue)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by Product"
                                size="small"
                            />
                        )}
                    />

                </Grid>

                {/* CUSTOMER */}
                <Grid className="col-sm-3 mb-2">

                    <Autocomplete
                        options={customerList}

                        value={selectedCustomer}

                        onChange={(event, newValue) => {
                            setSelectedCustomer(newValue);
                        }}

                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }

                        getOptionLabel={(option) =>
                            option?.customerName ||
                            option?.name ||
                            ""
                        }

                        filterOptions={(options, state) => {

                            const input =
                                state.inputValue.toLowerCase();

                            return options.filter((option) => {

                                const name =
                                    option.customerName ||
                                    option.name ||
                                    "";

                                const mobile =
                                    option.mobile || "";

                                return (
                                    name.toLowerCase().includes(input) ||
                                    mobile.toLowerCase().includes(input)
                                );

                            });

                        }}

                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>

                                <Box>

                                    <Typography variant="body2">
                                        {option.customerName || option.name}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {option.mobile || ""}
                                    </Typography>

                                </Box>

                            </li>
                        )}

                        renderInput={(params) => (

                            <TextField
                                {...params}
                                label="Filter by Customer"
                                size="small"
                                fullWidth
                            />

                        )}
                    />

                </Grid>

                {/* FROM DATE */}
                <Grid className="col-sm-2 mb-2">

                    <TextField
                        fullWidth
                        label="From"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={fromDate}
                        onChange={(e) =>
                            setFromDate(e.target.value)
                        }
                    />

                </Grid>

                {/* TO DATE */}
                <Grid className="col-sm-2 mb-2">

                    <TextField
                        fullWidth
                        label="To"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={toDate}
                        onChange={(e) =>
                            setToDate(e.target.value)
                        }
                    />

                </Grid>

                {/* BUTTONS */}
                <Grid className="col-sm-2 mb-2">

                    <Button
                        variant="contained"
                        onClick={fetchReport}
                        disabled={loading}
                        sx={{ mr: 1 }}
                    >
                        {loading
                            ? <CircularProgress size={22} />
                            : "Generate"}
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={() => window.print()}
                    >
                        Print
                    </Button>

                </Grid>
            </Grid>

            {/* ================= REPORT ================= */}

            <Card sx={{ p: 2, mb: 3, mt: 2 }}>

                <Grid className="printable-area p-2">

                    <Box sx={{ border: '2px solid #000', p: 2 }}>

                        {/* HEADER */}
                        <Box textAlign="center" mb={2}>

                            <Typography
                                variant="h5"
                                fontWeight="bold"
                            >
                                MATOSHRI INFRA DHARASHIV PVT LTD
                            </Typography>

                            <Typography variant="body2">
                                Sarola, Dist. Dharashiv, Maharashtra
                            </Typography>

                            <Typography
                                variant="subtitle2"
                                sx={{
                                    mt: 1,
                                    fontWeight: 'bold',
                                    letterSpacing: 1
                                }}
                            >
                                PRODUCT WISE REPORT
                            </Typography>

                            <Typography variant="body2">
                                {fromDate} TO {toDate}
                            </Typography>

                            {selectedCustomer && (
                                <Typography
                                    variant="body1"
                                    fontWeight="bold"
                                >
                                    Customer :
                                    {" "}
                                    {selectedCustomer.customerName ||
                                        selectedCustomer.name}
                                </Typography>
                            )}

                            {selectedProduct && (
                                <Typography
                                    variant="body1"
                                    fontWeight="bold"
                                >
                                    Product :
                                    {" "}
                                    {selectedProduct.productName}
                                </Typography>
                            )}

                            <Typography
                                variant="body1"
                                sx={{
                                    mt: 1,
                                    fontWeight: "bold"
                                }}
                            >
                                Total Trips : {totalTrips}
                            </Typography>

                            <Divider
                                sx={{
                                    my: 2,
                                    bgcolor: '#000',
                                    height: '2px'
                                }}
                            />

                        </Box>

                        {/* TABLE */}
                        <Paper
                            sx={{
                                width: '100%',
                                overflow: 'hidden'
                            }}
                        >

                            <Table
                                size="small"
                                sx={{
                                    border: "2px solid #000",

                                    "& th, & td": {
                                        border: "1px solid #000",
                                        padding: "6px"
                                    }
                                }}
                            >

                                <TableHead sx={{ bgcolor: "#f5f5f5" }}>

                                    <TableRow>
                                        <TableCell align="center">
                                            <strong>S.No.</strong>
                                        </TableCell>

                                        <TableCell>
                                            <strong>Ticket</strong>
                                        </TableCell>

                                        <TableCell>
                                            <strong>Party</strong>
                                        </TableCell>

                                        <TableCell>
                                            <strong>Vehicle</strong>
                                        </TableCell>

                                        <TableCell>
                                            <strong>Product</strong>
                                        </TableCell>

                                        {!hasBolder && (
                                            <>
                                                <TableCell align="right">
                                                    <strong>Qty</strong>
                                                </TableCell>

                                                <TableCell align="right">
                                                    <strong>Rate</strong>
                                                </TableCell>

                                                <TableCell align="right">
                                                    <strong>Amount</strong>
                                                </TableCell>
                                            </>
                                        )}

                                        <TableCell align="right">
                                            <strong>Tare</strong>
                                        </TableCell>

                                        <TableCell align="right">
                                            <strong>Gross</strong>
                                        </TableCell>

                                        <TableCell align="right">
                                            <strong>Net</strong>
                                        </TableCell>

                                    </TableRow>

                                </TableHead>

                                <TableBody>

                                    {reportData.map((row, index) => (

                                        <TableRow key={index}>
                                            <TableCell align="center">
                                                {index + 1}
                                            </TableCell>

                                            <TableCell>
                                                {row.ticketNo}
                                            </TableCell>

                                            <TableCell>
                                                {row.partyName}
                                            </TableCell>

                                            <TableCell>
                                                {row.vehicleNo}
                                            </TableCell>

                                            <TableCell>
                                                {row.productName}
                                            </TableCell>

                                            {!hasBolder && (
                                                <>
                                                    <TableCell align="right">
                                                        {row.qty}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        {row.rate}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        ₹{row.amount.toLocaleString()}
                                                    </TableCell>
                                                </>
                                            )}

                                            <TableCell align="right">
                                                {row.tareWeight}
                                            </TableCell>

                                            <TableCell align="right">
                                                {row.grossWeight}
                                            </TableCell>

                                            <TableCell align="right">
                                                {row.netWeight}
                                            </TableCell>

                                        </TableRow>
                                    ))}

                                    {/* TOTAL */}
                                    {reportData.length > 0 && (

                                        <TableRow sx={{ bgcolor: "#eee" }}>

                                            <TableCell
                                                colSpan={5}
                                                align="right"
                                            >
                                                <strong>
                                                    GRAND TOTAL
                                                </strong>
                                            </TableCell>

                                            {!hasBolder && (
                                                <>
                                                    <TableCell align="right">
                                                        <strong>
                                                            {totalQty}
                                                        </strong>
                                                    </TableCell>

                                                    <TableCell />

                                                    <TableCell align="right">
                                                        <strong>
                                                            ₹{totalAmt.toLocaleString()}
                                                        </strong>
                                                    </TableCell>
                                                </>
                                            )}

                                            <TableCell align="right">
                                                <strong>{totalTare}</strong>
                                            </TableCell>

                                            <TableCell align="right">
                                                <strong>{totalGross}</strong>
                                            </TableCell>

                                            <TableCell align="right">
                                                <strong>{totalNet}</strong>
                                            </TableCell>

                                        </TableRow>
                                    )}

                                    {reportData.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={12}
                                                align="center"
                                            >
                                                No data found
                                            </TableCell>
                                        </TableRow>
                                    )}

                                </TableBody>

                            </Table>

                            {/* FOOTER */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 10,
                                    mt: 10,
                                    width: "100%"
                                }}
                            >

                                <Typography fontWeight="bold">
                                    Tirupati Enterprises
                                </Typography>

                                <Typography fontWeight="bold">
                                    Matoshri Infra Dharashiv Pvt Ltd
                                </Typography>

                            </Box>

                        </Paper>

                    </Box>

                </Grid>

            </Card>

            {/* ================= PRINT ================= */}

            <style>{`
                @media print {

                    body * {
                        visibility: hidden;
                    }

                    .printable-area,
                    .printable-area * {
                        visibility: visible;
                    }

                    .printable-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        border: none !important;
                    }

                    .no-print {
                        display: none !important;
                    }

                    @page {
                        size: A4;
                        margin: 10mm;
                    }
                }
            `}</style>

        </Box>
    );
}

export default ProductwiseReport;